import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators,Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import MultipleSelection from "../../components/Editing/MultipleSelection";
import { getRoleOptions } from "../../utils/pdApiUtils";

type StateProps = {
  editedUser: Api.User;
  storedUser: Api.User;
  pdApi: State.PdApi | undefined;
  roles: Api.Role[] | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

interface EditUserState {
  errorMsg: string;
}

interface ErrorStatus{
  errorStatus: boolean;
  errorMsg: string;

}

const userFields = nameofFactory<Api.User>();

export type EditUserProps = StateProps & DispatchProps & OwnProps;

export class EditUser extends React.Component<EditUserProps, EditUserState> {
  constructor(props: EditUserProps) {
    super(props);
    this.state = {
      errorMsg: ""
    };
  }
  render(): JSX.Element {
    const { editedUser, storedUser, toggleModal } = this.props;
    const storedRoles = storedUser?.roles?.sort((a, b) => a.id - b.id);
    const editedRoles = editedUser?.roles?.sort((a, b) => a.id - b.id);
    if (storedRoles && editedRoles) {
      storedUser.roles = storedRoles;
      editedUser.roles = editedRoles;
    }
    const roleOptions = getRoleOptions(this.props.roles);
    const isNew = !storedUser;
    const error = this.isFieldValid(userFields("adAccount"));
    return (
      <Editor
        itemName={"user"}
        originalObject={storedUser}
        editedObject={editedUser}
        hasWritePermission={true}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("users")}
        create={() => this.props.editActions.add("users")}
        remove={{ callback: toggleModal }}
      >
        <TextField
          id="adAccount"
          label="Account"
          value={editedUser.adAccount}
          isError={error.errorStatus}
          errorMessage={error.errorMsg}
          onChange={adAccount => this.props.editActions.setEdited({ ...editedUser, adAccount })}
          disabled={!isNew}
        />
        <div className="spaced-flex-row">
          <MultipleSelection
            id="roles"
            label="Roles"
            value={editedUser.roles && editedUser.roles.map(role => role.id)}
            options={roleOptions}
            onChange={roleIds => {
              const roles = roleIds.map((id: number) => ({
                id,
                name: this.props.roles.find(item => item.id === id)?.name,
                description: this.props.roles.find(item => item.id === id)?.description
              }));
              this.props.editActions.setEdited({
                ...editedUser,
                roles
              });
            }}
          />
        </div>
      </Editor>
    );
  }

  private isFieldValid = (field: string): ErrorStatus => {
    const { pdApi, editedUser } = this.props;
    switch (field) {
      case userFields("adAccount"): {
        const { adAccount } = editedUser;
        if (this.isEmptyString(adAccount)) {
          return { errorStatus: true, errorMsg: "The account name cannot be empty" };
        } else if (this.checkEmail(editedUser.adAccount)) {
          return { errorStatus: true, errorMsg: "The account name must be a valid KONE" };
        } else {
          return _.values(pdApi.users).find(
            User =>
              User.id !== editedUser.id &&
              User.adAccount?.toLowerCase() === editedUser.adAccount?.trim().toLowerCase()
          )
            ? { errorStatus: true, errorMsg: "The account name must be unique." }
            : { errorStatus: false, errorMsg: "" };
        }
      }
      default:
        return { errorStatus: false, errorMsg: "" };
    }
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private checkEmail = (email: string): boolean => !/^[a-zA-Z0-9._%+-]+@kone\.com$/i.test(email);

  private isAllFieldsValid = (): boolean => {
    const { editedUser } = this.props;
    const temp = _.keys(editedUser);
    for (const key of temp) {
      if (this.isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  };

  /*
   * Toggles the named role from user. Roles are fetched from pdApi and then either
   * filtered from user or added to users existing roles.
   */
  private handleRoleChange = (roleName: string): void => {
    const { editedUser, roles } = this.props;
    // Find the new role from Api Roles
    const newRole = roles.find(role => role.name === roleName) as Api.Role;
    let newRoles: Api.Role[];
    // If user already has the role remove it else add id
    if (newRole) {
      editedUser.roles.find(role => role.name === roleName)
        ? (newRoles = editedUser.roles.filter(role => role.name !== roleName))
        : (newRoles = [...editedUser.roles, newRole]);
      this.props.editActions.setEdited({ ...editedUser, roles: newRoles });
    }
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedUser: state.editView.editedObject,
  storedUser: state.editView.storedObject,
  pdApi: state.pdApi,
  roles: state.pdApi?.roles
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditUser);
